import { useEffect, useState } from 'react';
import { AlertTriangle, ArrowRight, CheckCircle, FileText, AlertCircle, Info, XCircle, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
/**
 * COMMERCIAL SCOPE DELTA REVIEW
 *
 * This component allows consultants to review detected scope creep and
 * make commercial decisions (e.g. "Prepare Change Order").
 *
 * It feeds into the Commercial Change Order Builder.
 */

import { toast } from 'sonner';
import { projectId } from '../utils/supabase/info';
import { ChangeOrderBuilder } from './ChangeOrderBuilder';

// Re-defined interface since we cannot import from server functions directly in frontend
export interface ScopeDelta {
  deltaId: string;
  relatedScopeItemId?: string;
  sourceArtifact: string;
  description: string;
  deltaType: "addition" | "expansion" | "complexity";
  estimatedImpact: "small" | "medium" | "large";
  confidence: "low" | "medium" | "high";
  detectedAt: string;
}

export interface ScopeDeltaDecision {
  deltaId: string;
  decision: "reduce_other_scope" | "client_handles" | "prepare_change_order";
  notes?: string;
  decidedBy?: string;
  decidedAt: string;
}

interface ScopeDeltaReviewProps {
  companyId: string;
  configItemId?: string;
  itemId?: string; // Alias for configItemId
  deltas?: ScopeDelta[];
  existingDecisions?: Record<string, ScopeDeltaDecision>;
  userId?: string;
  userName?: string;
  currentUser?: any; // { id, name, email }
  accessToken?: string;
}

type DeltaAction = 'reduce_other_scope' | 'client_handles' | 'prepare_change_order' | null;

export function ScopeDeltaReview({ 
  companyId, 
  configItemId,
  itemId,
  deltas: propDeltas, 
  existingDecisions: propDecisions = {},
  userId: propUserId,
  userName: propUserName,
  currentUser,
  accessToken
}: ScopeDeltaReviewProps) {
  const finalConfigItemId = configItemId || itemId;
  const finalUserId = propUserId || currentUser?.id;
  const finalUserName = propUserName || currentUser?.name || currentUser?.email;

  // Map existing decisions to simple state map for UI
  const [decisions, setDecisions] = useState<Record<string, DeltaAction>>(() => {
    const map: Record<string, DeltaAction> = {};
    if (propDecisions) {
      Object.values(propDecisions).forEach(d => {
        map[d.deltaId] = d.decision;
      });
    }
    return map;
  });
  
  const [localDeltas, setLocalDeltas] = useState<ScopeDelta[]>(propDeltas || []);
  const [loading, setLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState<Record<string, boolean>>({});
  const [isDraftingCO, setIsDraftingCO] = useState(false);

  // Fetch data if deltas are not provided
  useEffect(() => {
    if (propDeltas) {
      setLocalDeltas(propDeltas);
      return;
    }

    if (!finalConfigItemId || !accessToken) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch Deltas
        const deltasRes = await fetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config-items/${companyId}/${finalConfigItemId}/artifacts/scopeDelta`,
          {
            headers: { 'Authorization': `Bearer ${accessToken}` }
          }
        );
        
        let fetchedDeltas: ScopeDelta[] = [];
        if (deltasRes.ok) {
          fetchedDeltas = await deltasRes.json();
          setLocalDeltas(fetchedDeltas);
        }

        // Fetch Decisions (if possible, or rely on passed prop)
        // Currently there isn't a clear endpoint to just get decisions without the full artifact, 
        // but often the artifact endpoint returns everything.
        // Assuming for now we just load deltas. 
        // If decisions are persisted in the delta artifact, they might be there.
        // But usually decisions are separate.
        // For now, if propDecisions is empty and we are fetching mode, we might miss existing decisions.
        // Let's check if the backend returns decisions in the same call or if we can fetch them.
        // The artifacts/scopeDelta endpoint usually returns the array of deltas.
        
        // Try to fetch decisions if we can
        // const decisionsRes = await fetch(...) 
        // For now, we proceed with fetched deltas.
        
      } catch (error) {
        console.error("Error fetching scope deltas:", error);
        toast.error("Failed to load scope details");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [propDeltas, finalConfigItemId, accessToken, companyId]);

  // Update decisions state when propDecisions changes (if provided)
  useEffect(() => {
    if (propDecisions) {
      setDecisions(prev => {
        const map: Record<string, DeltaAction> = {};
        Object.values(propDecisions).forEach(d => {
          map[d.deltaId] = d.decision;
        });
        return { ...prev, ...map };
      });
    }
  }, [propDecisions]);

  const handleDecision = async (deltaId: string, action: DeltaAction) => {
    if (!finalConfigItemId || !finalUserId) {
        toast.error("Missing user or item information");
        return;
    }

    // If action is null (resetting), we allow it UI-wise but backend tracks history.
    // For now we assume "Change" button allows picking a new decision.
    if (!action) {
       setDecisions(prev => {
         const next = { ...prev };
         delete next[deltaId];
         return next;
       });
       return;
    }

    setIsUpdating(prev => ({ ...prev, [deltaId]: true }));
    
    // Optimistic UI update
    setDecisions(prev => ({
      ...prev,
      [deltaId]: action
    }));

    try {
      const response = await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config-items/${finalConfigItemId}/scope-deltas/decisions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken || localStorage.getItem('supabase.auth.token') || ''}`,
        },
        body: JSON.stringify({
          companyId,
          deltaId,
          decision: action,
          userId: finalUserId,
          userName: finalUserName,
          notes: ''
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save decision');
      }
      
      toast.success('Decision saved');
    } catch (error) {
      console.error('Error saving decision:', error);
      toast.error('Failed to save decision');
      // Revert on error
      setDecisions(prev => {
         const next = { ...prev };
         delete next[deltaId]; // or revert to previous if we tracked it
         return next;
       });
    } finally {
      setIsUpdating(prev => ({ ...prev, [deltaId]: false }));
    }
  };

  if (isDraftingCO && finalUserId && finalUserName && finalConfigItemId) {
     const coDeltas = localDeltas.filter(d => decisions[d.deltaId] === 'prepare_change_order');
     return (
        <ChangeOrderBuilder 
            selectedDeltas={coDeltas}
            companyId={companyId}
            configItemId={finalConfigItemId}
            userId={finalUserId}
            userName={finalUserName}
            onBack={() => setIsDraftingCO(false)}
        />
     );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (localDeltas.length === 0) {
    return (
      <div className="text-center p-8 border rounded-lg bg-muted/20">
        <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium">Scope is on track</h3>
        <p className="text-muted-foreground mt-2">No discrepancies detected between SOW baseline and discovery findings.</p>
      </div>
    );
  }

  const hasChangeOrderItems = Object.values(decisions).some(d => d === 'prepare_change_order');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-orange-500" />
            Scope Creep Detected
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            The following items appear to differ from the signed SOW. Review and classify them.
          </p>
        </div>
        <div className="flex gap-3">
            <Badge variant="outline" className="px-3 py-1 h-8">
              {localDeltas.length} Issues Found
            </Badge>
            {hasChangeOrderItems && (
                <Button size="sm" variant="default" onClick={() => setIsDraftingCO(true)}>
                    <FileText className="w-4 h-4 mr-2" />
                    Draft Change Order
                </Button>
            )}
        </div>
      </div>

      <div className="grid gap-4">
        {localDeltas.map((delta) => {
          const decision = decisions[delta.deltaId];
          
          return (
            <Card key={delta.deltaId} className={`border-l-4 ${getBorderColor(delta.estimatedImpact)}`}>
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant={getImpactVariant(delta.estimatedImpact)}>
                        {delta.estimatedImpact.toUpperCase()} IMPACT
                      </Badge>
                      <Badge variant="outline" className="text-xs font-normal">
                        {delta.deltaType.toUpperCase()}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        Confidence: {delta.confidence}
                      </span>
                    </div>
                    <CardTitle className="text-base leading-snug">{delta.description}</CardTitle>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="pb-3">
                <div className="text-sm text-muted-foreground bg-muted/30 p-3 rounded-md flex items-start gap-2">
                  <Info className="w-4 h-4 mt-0.5 shrink-0" />
                  <div>
                    <span className="font-medium text-foreground">Detected via {delta.sourceArtifact}:</span>
                    <span className="ml-1">
                       New findings contradict SOW baseline assumptions.
                    </span>
                  </div>
                </div>
              </CardContent>

              <Separator />

              <CardFooter className="pt-3 bg-muted/5 flex flex-wrap gap-2 justify-end">
                 {!decision ? (
                   <>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleDecision(delta.deltaId, 'reduce_other_scope')}
                      className="text-xs"
                      disabled={isUpdating[delta.deltaId]}
                    >
                      <ArrowRight className="w-3 h-3 mr-2" />
                      Reduce Other Scope
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleDecision(delta.deltaId, 'client_handles')}
                      className="text-xs"
                      disabled={isUpdating[delta.deltaId]}
                    >
                      <XCircle className="w-3 h-3 mr-2" />
                      Client Handles This
                    </Button>
                    <Button 
                      variant="destructive" 
                      size="sm" 
                      onClick={() => handleDecision(delta.deltaId, 'prepare_change_order')}
                      className="text-xs"
                      disabled={isUpdating[delta.deltaId]}
                    >
                      <FileText className="w-3 h-3 mr-2" />
                      Prepare Change Order
                    </Button>
                   </>
                 ) : (
                   <div className="flex items-center justify-between w-full">
                     <span className="text-sm font-medium flex items-center gap-2">
                       {getActionIcon(decision)}
                       Decision: {getActionLabel(decision)}
                     </span>
                     <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleDecision(delta.deltaId, null)}
                        className="h-8 px-2 text-muted-foreground"
                     >
                       Change
                     </Button>
                   </div>
                 )}
              </CardFooter>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function getBorderColor(impact: string) {
  switch (impact) {
    case 'large': return 'border-l-red-500';
    case 'medium': return 'border-l-orange-500';
    default: return 'border-l-blue-500';
  }
}

function getImpactVariant(impact: string): "default" | "destructive" | "outline" | "secondary" {
  switch (impact) {
    case 'large': return 'destructive';
    case 'medium': return 'default'; // dark/primary
    default: return 'secondary';
  }
}

function getActionLabel(action: DeltaAction) {
  switch (action) {
    case 'reduce_other_scope': return 'Trade-off (Reduce Scope)';
    case 'client_handles': return 'Client Responsibility';
    case 'prepare_change_order': return 'Change Order Required';
    default: return 'Pending';
  }
}

function getActionIcon(action: DeltaAction) {
   switch (action) {
    case 'reduce_other_scope': return <ArrowRight className="w-4 h-4 text-blue-500" />;
    case 'client_handles': return <XCircle className="w-4 h-4 text-gray-500" />;
    case 'prepare_change_order': return <FileText className="w-4 h-4 text-red-500" />;
    default: return null;
  }
}
